import {
  ADMIN_FEATURE_FAIL_CLOSED,
  ADMIN_FEATURE_IDS,
  AdminFeatureConfig,
  AdminFeatureId,
} from "@equipped/core/admin/adminFeatures";
import { AdminDeploymentEnv, resolveAdminDeploymentKind } from "./adminDeployment.config";

/**
 * The frontend half of docs/358-ADMIN-FEATURE-CONFIGURATION.md v0 — resolves the
 * build-time `AdminFeatureConfig` (docs/358 §2/§10 Q1: "a bundled TS constant selected
 * by a Vite env var" for v0). `VITE_ADMIN_DEPLOYMENT` unset or `"owner"` → `{ mode:
 * "all" }` — the owner's own deployment, everything on, needing NO env config at all
 * (docs/358 §6). `"provider"` reads `VITE_ADMIN_FEATURES` as a JSON array of
 * `AdminFeatureId`s — a per-tenant `deploy-tenant.sh` (docs/344 §3) injects it at build
 * time. A malformed/missing allow-list on a `"provider"` build fails CLOSED to
 * {@link ADMIN_FEATURE_FAIL_CLOSED} (docs/358 §2/§10 Q5) rather than silently exposing
 * a sensitive feature.
 */

const ADMIN_FEATURE_ID_SET: ReadonlySet<string> = new Set(ADMIN_FEATURE_IDS);

function isAdminFeatureId(value: unknown): value is AdminFeatureId {
  return typeof value === "string" && ADMIN_FEATURE_ID_SET.has(value);
}

/** Parses `VITE_ADMIN_FEATURES` — a JSON array of feature ids. Returns `null` on any
 * parse/shape failure (malformed env, never thrown at import time). */
function parseFeatureList(raw: string): AdminFeatureId[] | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!Array.isArray(parsed)) return null;
  return parsed.filter(isAdminFeatureId);
}

/** Pure — resolves the deployment's `AdminFeatureConfig` from a Vite-`import.meta.env`
 * shaped object. Exported for testability even though this app has no test runner
 * wired today (docs/337); the logic itself is deliberately simple/inspectable. */
export function resolveConfigFromEnv(env: AdminDeploymentEnv): AdminFeatureConfig {
  const deployment = resolveAdminDeploymentKind(env);
  if (deployment !== "provider") return { mode: "all" };
  const parsed = env.VITE_ADMIN_FEATURES ? parseFeatureList(env.VITE_ADMIN_FEATURES) : null;
  return {
    mode: "allow",
    features: parsed && parsed.length > 0 ? parsed : ADMIN_FEATURE_FAIL_CLOSED,
  };
}

export const ADMIN_FEATURE_CONFIG: AdminFeatureConfig = resolveConfigFromEnv(import.meta.env);
